import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  generateTrendReport,
  getLatestTrendReport,
} from 'services/vendorAPI';
import { getReportDate } from 'utils/trendReportUtils';

const TrendReportContext = createContext(null);

const showBrowserNotification = (body) => {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    new Notification('Sportona', {
      body,
      icon: '/favicon.ico',
    });
  } catch {
    // Ignore unsupported environments
  }
};

export const TrendReportProvider = ({ children }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle');
  const [readyReport, setReadyReport] = useState(null);

  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const generationStartedAtRef = useRef(null);
  const readyListenersRef = useRef(new Set());

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const notifyReadyListeners = useCallback((report) => {
    readyListenersRef.current.forEach((listener) => {
      try {
        listener(report);
      } catch {
        // Ignore listener errors
      }
    });
  }, []);

  const handleTrendReportReady = useCallback(
    (payload) => {
      const report = payload?.data ?? payload?.report ?? payload;
      stopPolling();
      setStatus('idle');
      setReadyReport(report);
      notifyReadyListeners(report);

      toast.success(
        (t) => (
          <div className="flex flex-col gap-1">
            <span className="font-bold">Trend report ready</span>
            <span className="text-sm">Your new trend analysis is available.</span>
            <button
              type="button"
              onClick={() => {
                toast.dismiss(t.id);
                navigate('/trends');
              }}
              className="mt-1 text-left text-sm font-bold underline"
            >
              View Report →
            </button>
          </div>
        ),
        { duration: 8000 },
      );

      showBrowserNotification('Your trend report is ready!');
    },
    [navigate, notifyReadyListeners, stopPolling],
  );

  const handleTrendReportStarted = useCallback((data) => {
    setStatus('generating');
    toast(
      data?.message || 'Report generation started. We will notify you when it is ready.',
      {
        icon: '⏳',
        duration: 5000,
      },
    );
  }, []);

  const handleTrendReportFailed = useCallback(
    (data) => {
      stopPolling();
      setStatus('idle');
      toast.error(data?.error || 'Report generation failed. Please try again.', {
        duration: 6000,
      });
    },
    [stopPolling],
  );

  const startPolling = useCallback(
    (startedAt) => {
      stopPolling();

      pollIntervalRef.current = setInterval(async () => {
        const res = await getLatestTrendReport();
        if (!res.success) return;

        const report = res.data?.report || res.data?.data || res.data;
        const reportDate = getReportDate(report);
        if (reportDate && reportDate > startedAt) {
          handleTrendReportReady(report);
        }
      }, 15000);

      pollTimeoutRef.current = setTimeout(() => {
        stopPolling();
        setStatus('idle');
      }, 180000);
    },
    [handleTrendReportReady, stopPolling],
  );

  const generateReport = useCallback(async () => {
    const startedAt = new Date();
    generationStartedAtRef.current = startedAt;
    setStatus('generating');

    try {
      const res = await generateTrendReport();

      if (res.success) {
        const message =
          res.data?.message ||
          res.data?.data?.message ||
          'Report generation started. We will notify you when it is ready.';
        toast(message, { icon: '⏳', duration: 5000 });
        startPolling(startedAt);
        return { success: true };
      }

      setStatus('idle');
      stopPolling();

      if (res.status === 429) {
        toast.error('You can only generate one report every 24 hours.', { duration: 5000 });
      } else {
        toast.error(res.error || 'Failed to start report generation.');
      }
      return { success: false, error: res.error, status: res.status };
    } catch {
      setStatus('idle');
      stopPolling();
      toast.error('An unexpected error occurred.');
      return { success: false };
    }
  }, [startPolling, stopPolling]);

  const subscribeToReady = useCallback((listener) => {
    readyListenersRef.current.add(listener);
    return () => readyListenersRef.current.delete(listener);
  }, []);

  const value = useMemo(
    () => ({
      status,
      readyReport,
      generateReport,
      subscribeToReady,
      handleTrendReportStarted,
      handleTrendReportReady,
      handleTrendReportFailed,
    }),
    [
      status,
      readyReport,
      generateReport,
      subscribeToReady,
      handleTrendReportStarted,
      handleTrendReportReady,
      handleTrendReportFailed,
    ],
  );

  return (
    <TrendReportContext.Provider value={value}>{children}</TrendReportContext.Provider>
  );
};

export const useTrendReport = () => {
  const context = useContext(TrendReportContext);
  if (!context) {
    throw new Error('useTrendReport must be used within a TrendReportProvider');
  }
  return context;
};

export default TrendReportContext;
